import React, { useState } from 'react';

import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { sendEventToGA } from 'v2/helpers/analytics';
import { numberToBRL } from 'v2/helpers/money';
import { TransferStatus, friendilyAntifraudStatus, type TransferHistoryEntry } from 'v2/interfaces/financial';
import type { IPaginationResponse } from 'v2/interfaces/pagination';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { InitialPagination } from 'v2/services/transferHistory';

import CompositionModal from '../CompositionModal';

interface IContentTableProps {
  data: TransferHistoryEntry[];
  pagination: IPaginationResponse;
  handlePagination: {
    onChangeRowsPerPage: (per_page: number) => void;
    onChangePage: (page: number) => void;
  };
  loading: boolean;
}

const getStatusTagClasses = (statusName: string) => {
  if (statusName === 'Cancelada') return 'bg-red-100 text-red-800';
  if (statusName === 'Confirmada') return 'bg-green-100 text-green-800';
  return 'bg-gray-100 text-gray-800';
};

function CancelledTransferLinkKnowMore() {
  return (
    <>
      <a
        className='cursor-pointer text-blue-600 hover:underline'
        href='https://ajuda.eduzz.com/hc/pt-br/articles/6734291431067-Estou-com-problemas-nas-minhas-transfer%C3%AAncias-e-agora'
        target='_blank'
        rel='noopener noreferrer'
      >
        Clique aqui
      </a>{' '}
      e saiba mais.
    </>
  );
}

function ContentTable({ data, pagination, handlePagination, loading }: IContentTableProps) {
  const [openCompositionModal, setOpenCompositionModal] = useState<boolean>(false);
  const [currentCompositionId, setCurrentCompositionId] = useState<number | null>(null);

  const isBetaInvoiceTransfer = eduzzAccountsService.verifyUserHasBetaModule('transferencia_fatura');

  const formatDate = (dateRaw: string) => new Intl.DateTimeFormat('pt-BR').format(new Date(`${dateRaw}T00:00:00`));

  const handleToggleCompositionModal = () => setOpenCompositionModal(prevState => !prevState);

  const handleClickCompositionReport = (compositionId: number) => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Botão',
      label: 'Composição de saque'
    });

    setCurrentCompositionId(compositionId);
    handleToggleCompositionModal();
  };

  function getDetails(entry: TransferHistoryEntry) {
    const { id, status_id, status_name, antifraud_status_name, can_export_transfer_data } = entry;

    const canViewTransferReport =
      can_export_transfer_data && status_id === TransferStatus.CONFIRMADA && !!isBetaInvoiceTransfer;

    if (status_name === 'Em Andamento') return 'Sua solicitação de transferência foi recebida e está em andamento.';
    if (status_name === 'Em Processamento')
      return 'A solicitação de transferência foi recebida e está em fase de processamento.';

    if (status_name === 'Confirmada' && canViewTransferReport) {
      return (
        <a
          id={`composition-${id}`}
          className='cursor-pointer text-blue-600 hover:underline'
          role='button'
          onClick={() => handleClickCompositionReport(id)}
        >
          Composição de Saque
        </a>
      );
    }

    if (status_name === 'Confirmada' && !canViewTransferReport) return 'Transferência realizada com sucesso.';
    if (status_name === 'Pendente') return 'O prazo para transferência bancária é de 1 dia útil.';

    if (status_name === 'Cancelada') {
      if (antifraud_status_name) {
        return (
          <>
            {friendilyAntifraudStatus[antifraud_status_name as keyof typeof friendilyAntifraudStatus]}&nbsp;
            {antifraud_status_name !== 'Cancelado Solicitação do Cliente' && <CancelledTransferLinkKnowMore />}
          </>
        );
      }

      return (
        <>
          Falha na transferência.&nbsp;
          <CancelledTransferLinkKnowMore />
        </>
      );
    }

    return '';
  }

  const columns: ColumnsType<TransferHistoryEntry> = [
    {
      title: 'Data da solicitação',
      dataIndex: 'solicitation_date',
      width: 180,
      render: (date: string) => formatDate(date)
    },
    {
      title: 'Data de conclusão',
      dataIndex: 'conclusion_date',
      width: 180,
      render: (date: string) => (date ? formatDate(date) : '')
    },
    {
      title: 'Valor',
      dataIndex: 'value',
      width: 140,
      render: (value: number) => numberToBRL(value)
    },
    {
      title: 'Status',
      dataIndex: 'status_name',
      width: 200,
      align: 'center',
      render: (statusName: string) => (
        <span className={`rounded-sm px-2 py-1 text-xs font-medium ${getStatusTagClasses(statusName)}`}>
          {statusName}
        </span>
      )
    },
    {
      title: 'Detalhes',
      key: 'details',
      render: (_: unknown, entry: TransferHistoryEntry) => getDetails(entry)
    }
  ];

  const showPagination = !loading && (pagination?.total_pages > 1 || pagination?.per_page > InitialPagination.per_page);

  return (
    <>
      <Table<TransferHistoryEntry>
        id='transfers-table'
        columns={columns}
        dataSource={data}
        rowKey={entry => `TransferItem-${entry.id}`}
        loading={loading}
        pagination={
          showPagination
            ? {
                current: pagination?.current_page || 1,
                pageSize: pagination?.per_page || 15,
                total: pagination?.total_items || 0,
                pageSizeOptions: [15, 30, 50, 100],
                showSizeChanger: true,
                onChange: (page: number) => handlePagination.onChangePage(page),
                onShowSizeChange: (_: number, size: number) => handlePagination.onChangeRowsPerPage(size)
              }
            : false
        }
      />
      <CompositionModal
        open={openCompositionModal}
        setClose={handleToggleCompositionModal}
        compositionId={currentCompositionId}
      />
    </>
  );
}

export default ContentTable;
